/**
 * B1 (E-1.C.2-01): Wholesale Pricing Configuration
 *
 * Per-tier wholesale/retail pricing with margin calculator.
 * Agency sets wholesale (cost from Ease) and retail (what they charge clients).
 */
import { useState, useEffect } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface PlanPricing {
  slug: string;
  name: string;
  wholesalePriceCents: number;
  retailPriceCents: number;
  marginCents: number;
  marginPercent: number;
}

const PLAN_TIERS = [
  { slug: "starter", nameKey: "billing.starter" },
  { slug: "growth", nameKey: "billing.growth" },
  { slug: "scale", nameKey: "billing.scale" },
];

export default function WholesalePricingPage() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<PlanPricing[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    apiFetch<{ plans: PlanPricing[] }>("/platform/billing/wholesale-pricing")
      .then((res) => setPlans(res.plans))
      .catch(() => {
        // Default pricing
        setPlans(PLAN_TIERS.map((tier) => ({
          slug: tier.slug,
          name: t(tier.nameKey),
          wholesalePriceCents: 0,
          retailPriceCents: 0,
          marginCents: 0,
          marginPercent: 0,
        })));
      });
  }, []);

  const updatePricing = (slug: string, field: "wholesalePriceCents" | "retailPriceCents", cents: number) => {
    setPlans((prev) => prev.map((p) => {
      if (p.slug !== slug) return p;
      const updated = { ...p, [field]: cents };
      updated.marginCents = updated.retailPriceCents - updated.wholesalePriceCents;
      updated.marginPercent = updated.retailPriceCents > 0
        ? Math.round((updated.marginCents / updated.retailPriceCents) * 100)
        : 0;
      return updated;
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiFetch("/platform/billing/wholesale-pricing", {
        method: "PUT",
        body: JSON.stringify({ plans }),
      });
    } catch {
      // Error handled by apiFetch
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t("billing.wholesale.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("billing.wholesale.description")}</p>
      </div>

      <div className="rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-left">
              <th className="px-4 py-3 font-medium">{t("billing.wholesale.plan")}</th>
              <th className="px-4 py-3 font-medium">{t("billing.wholesale.wholesale")}</th>
              <th className="px-4 py-3 font-medium">{t("billing.wholesale.retail")}</th>
              <th className="px-4 py-3 font-medium">{t("billing.wholesale.margin")}</th>
              <th className="px-4 py-3 font-medium">{t("billing.wholesale.marginPct")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {plans.map((plan) => (
              <tr key={plan.slug}>
                <td className="px-4 py-3 font-medium text-foreground">{plan.name}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">€</span>
                    <input
                      type="number"
                      value={plan.wholesalePriceCents / 100}
                      onChange={(e) => updatePricing(plan.slug, "wholesalePriceCents", Math.round(parseFloat(e.target.value) * 100))}
                      className="w-24 rounded border border-input bg-background px-2 py-1 text-right"
                      min={0}
                      step={1}
                    />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">€</span>
                    <input
                      type="number"
                      value={plan.retailPriceCents / 100}
                      onChange={(e) => updatePricing(plan.slug, "retailPriceCents", Math.round(parseFloat(e.target.value) * 100))}
                      className="w-24 rounded border border-input bg-background px-2 py-1 text-right"
                      min={0}
                      step={1}
                    />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={cn("font-semibold", plan.marginCents >= 0 ? "text-[var(--color-success)]" : "text-[var(--color-destructive)]")}>
                    {formatCurrency(plan.marginCents, "EUR", { maximumFractionDigits: 0 })}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={cn(
                    "rounded-full px-2 py-0.5 text-xs font-medium",
                    plan.marginPercent >= 30 ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" :
                    plan.marginPercent >= 10 ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]" :
                    "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
                  )}>
                    {plan.marginPercent}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving}
        className="rounded-md bg-primary px-6 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
      >
        {isSaving ? t("common.loading") : t("common.save")}
      </button>
    </div>
  );
}
